import { useRollbarPerson } from '@rollbar/react';
import { useCurrentUserState } from 'hooks/useCurrentUser';
import type { FC } from 'react';
import { Navigate } from 'react-router-dom';

import { checkIfTokenIsValid } from '../authStorage';
import { isFederatedAuthPopupReturn } from './authRouteUtils';
import routes, { type Role, type Route as RouteType } from './routes';

export { isFederatedAuthPopupReturn } from './authRouteUtils';

interface Props {
  route: RouteType;
}

const AuthRoute: FC<Props> = ({ route }) => {
  const { component: Component, path, private: privateRoute } = route;
  const { user, isLoading: isCurrentUserLoading } = useCurrentUserState();
  const isAdmin = !!user?.isAdmin;
  const isBusinessUnitAdmin = user?.role === 'businessUnitAdmin' || isAdmin;
  const isBranchManager = user?.role === 'branchManager' || isBusinessUnitAdmin;
  const isSeller = !isBranchManager && !isBusinessUnitAdmin && !isAdmin;
  const isSellerWithAssignment = user?.role === 'sellerWithAssignment';

  const getUserRoles = (): Role[] => {
    const roles: Role[] = [];
    if (isAdmin) roles.push('admin');
    if (isBusinessUnitAdmin) roles.push('businessUnitAdmin');
    if (isBranchManager) roles.push('branchManager');
    if (isSeller) roles.push('seller');
    if (isSellerWithAssignment) roles.push('sellerWithAssignment');
    return roles;
  };

  const hasRequiredRole = (requiredRoles?: Role[]): boolean => {
    if (!requiredRoles) return true;
    const userRoles = getUserRoles();
    return userRoles.some((role) => requiredRoles.includes(role));
  };

  if (user) {
    useRollbarPerson({
      id: user.id,
      email: user.email,
    });
  }
  const getValidRedirectUrl = (redirectUrl: string): string => {
    // the redirectUrl could be something like: /redirect-path&param=true for this url to work and keep the params
    // the first & needs to be replaced with ?
    const redirectUrlPath = redirectUrl.split('?')[0].split('&')[0];
    const validRedirectUrl = Object.values(routes).some(
      (routeValue) =>
        routeValue.path === redirectUrlPath ||
        routeValue.regex?.test(redirectUrlPath) ||
        // To handle redirect route to variable route
        redirectUrl.includes(routeValue.path.split(':')[0])
    );
    const validUrl = validRedirectUrl ? redirectUrl.replace('&', '?') : '/';
    if (!validUrl || validUrl === '/') {
      return '/clients';
    }
    return validUrl;
  };

  // Redirect from landing page since it's now on relif.com
  // app.relif.com should redirect to sign-in (if not logged in) or clients (if logged in)
  if (path === '/') {
    // Hold while an MSAL "Sign in with Microsoft" popup is handing its OAuth response back on this
    // origin — navigating away would strip it and silently break the login.
    if (isFederatedAuthPopupReturn(window.location.search + window.location.hash)) {
      return null;
    }
    const isUserValid = checkIfTokenIsValid(user);
    if (isUserValid) {
      return <Navigate to="/clients" />;
    }
    return <Navigate to="/sign-in" />;
  }

  const userIsValid = checkIfTokenIsValid(user);
  if (userIsValid && path === '/sign-in' && window.location.pathname === path) {
    // Extract redirect parameter from URL
    const searchParams = new URLSearchParams(window.location.search);
    const redirectParam = searchParams.get('redirect');
    const redirectUrl = redirectParam || '/';
    return <Navigate to={getValidRedirectUrl(redirectUrl)} />;
  }
  if (privateRoute && !userIsValid) {
    const signInPath = `/sign-in?redirect=${window.location.pathname}${window.location.search}`;
    return <Navigate to={signInPath} />;
  }
  const isCurrentlyAuthorized = hasRequiredRole(route.role);
  if (privateRoute && userIsValid && route.role && isCurrentUserLoading && !isCurrentlyAuthorized) {
    return null;
  }
  if (privateRoute && userIsValid && route.role) {
    if (!isCurrentlyAuthorized) {
      return <Navigate to="/unauthorized" />;
    }
    return <Component />;
  }
  return <Component />;
};

export default AuthRoute;
